import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import LotusLoader from "../components/LotusLoader";
import Particles from "../components/Particles";
import { INTENTIONS, buildPrescription } from "../lib/crystalPrescription";
import { fullNumerology, calcZodiac } from "../lib/numerology";
import { determineDominantElement, selectPanchaBeads } from "../lib/panchaEngine";
import { quizApi } from "../lib/api";
import CountryPhoneInput from "../components/CountryPhoneInput";
import { useSEO } from "../lib/seo";

const ZODIAC_LIST = [
  "Aries", "Taurus", "Gemini", "Cancer", "Leo", "Virgo",
  "Libra", "Scorpio", "Sagittarius", "Capricorn", "Aquarius", "Pisces",
];

const WRIST_OPTIONS = [
  { key: "small",   label: "Small",   sub: "14-15 cm" },
  { key: "average", label: "Average", sub: "16-17 cm" },
  { key: "large",   label: "Large",   sub: "18-19 cm" },
];

// Cap on healing intentions the customer can pick. Also enforced server-side
// (see backend/server.py quiz_step3 + create_order) and in the bracelet engine.
const MAX_INTENTIONS = 3;

export default function PrescriptionForm({ resumeData = null, initialStep = 1, isResume = false }) {
  useSEO({
    title: "Get Your Crystal Prescription | Pancha Healing",
    description:
      "Take our 2-minute Vedic prescription quiz. Get a personalised crystal bracelet mapped to your numerology, zodiac and five elements — with a printable Sacred Report.",
    canonical: "https://panchahealing.com/prescription",
  });
  const navigate = useNavigate();
  const [step, setStep] = useState(Math.min(2, Math.max(1, initialStep)));
  const [slideDir, setSlideDir] = useState("next");
  const [orderId, setOrderId] = useState(resumeData?.orderId || null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showResumeBanner, setShowResumeBanner] = useState(isResume);

  const [form, setForm] = useState(() => {
    // Widget prefill: if the landing-page calculator wrote sessionStorage,
    // honour it as an initial value (only when there's no resumeData).
    let widget = null;
    if (!resumeData && typeof window !== "undefined") {
      try {
        const raw = sessionStorage.getItem("pancha.widget_prefill");
        if (raw) widget = JSON.parse(raw);
      } catch (_e) { /* ignore */ }
    }
    const fName = resumeData?.fullName || widget?.fullName || "";
    const dob = resumeData?.dob || widget?.dob || "";
    return {
      fullName: fName,
      email: resumeData?.email || "",
      whatsapp: resumeData?.whatsapp || "",
      dob,
      zodiac: resumeData?.zodiac || (dob ? calcZodiac(dob) : ""),
      intentions: resumeData?.intentions || [],
      wristSize: resumeData?.wristSize || "",
    };
  });

  // Phone validity (driven by CountryPhoneInput)
  const [phoneState, setPhoneState] = useState({
    full: resumeData?.whatsapp || "",
    country: null,
    digits: "",
    isValid: false,
  });

  // Meta Pixel — track ViewContent once on prescription form mount
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "ViewContent");
    }
  }, []);

  const update = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "dob" && v) next.zodiac = calcZodiac(v) || next.zodiac;
      return next;
    });
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const toggleIntention = (label) => {
    setForm((f) => {
      const isActive = f.intentions.includes(label);
      // If not active and already at cap, ignore the tap (chip is also disabled visually).
      if (!isActive && f.intentions.length >= MAX_INTENTIONS) return f;
      return {
        ...f,
        intentions: isActive
          ? f.intentions.filter((i) => i !== label)
          : [...f.intentions, label],
      };
    });
    if (errors.intentions) setErrors((e) => ({ ...e, intentions: null }));
  };

  const prefilled = useMemo(() => {
    if (!isResume || !resumeData) return new Set();
    const s = new Set();
    if (resumeData.fullName) s.add("fullName");
    if (resumeData.email) s.add("email");
    if (resumeData.whatsapp) s.add("whatsapp");
    if (resumeData.dob) s.add("dob");
    if (resumeData.zodiac) s.add("zodiac");
    if (resumeData.wristSize) s.add("wristSize");
    return s;
  }, [isResume, resumeData?.orderId]);

  // ---------- Validation ----------
  // Both validators are invoked from useMemo (so they run during render). They use
  // `new Date()` / `Date.now()` which the strict purity rule disallows, but the
  // values are derived solely from `form.dob` + the current clock and there is no
  // observable side-effect — the result is recomputed only when dependencies change.
  const validateStep1 = () => {
    const e = {};
    if (!form.fullName.trim() || form.fullName.trim().length < 2) e.fullName = "Full name is required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Enter a valid email";
    if (!phoneState.isValid) {
      const expected = phoneState.country?.lengths?.join(" or ") || "10";
      e.whatsapp = phoneState.digits ? `Enter a valid phone (${expected} digits)` : "Phone is required";
    }
    if (!form.dob) e.dob = "Date of birth is required";
    else {
      const d = new Date(form.dob);
      if (isNaN(d.getTime()) || d.getTime() >= Date.now()) e.dob = "Must be a valid past date";
      else if (form.dob < "1930-01-01") e.dob = "Please enter a birth year 1930 or later";
    }
    return e;
  };
  const validateStep2 = () => {
    const e = {};
    if (!form.zodiac) e.zodiac = "Select your zodiac sign";
    if (form.intentions.length === 0) e.intentions = "Select at least one intention";
    if (!form.wristSize) e.wristSize = "Select a wrist size";
    return e;
  };

  const step1Valid = useMemo(() => Object.keys(validateStep1()).length === 0, [form.fullName, form.email, phoneState.isValid, phoneState.digits, form.dob]);
  const step2Valid = useMemo(() => Object.keys(validateStep2()).length === 0, [form.zodiac, form.intentions, form.wristSize]);

  // ---------- Submit handlers ----------
  const goNext = async () => {
    if (step === 1) {
      const e = validateStep1();
      if (Object.keys(e).length) { setErrors(e); return; }
      setSubmitting(true);
      try {
        let id = orderId;
        if (!id) {
          // Create new lead with contacts
          const r1 = await quizApi.step1({
            fullName: form.fullName.trim(),
            email: form.email.trim(),
            whatsapp: phoneState.full,
          });
          id = r1.id;
          setOrderId(id);
        }
        // Save DOB (also derives life_path + birth_month_crystal server-side)
        await quizApi.step2(id, { dob: form.dob });
        setSlideDir("next");
        setStep(2);
      } catch {
        toast.error("Couldn't save — try again");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (step === 2) {
      const e = validateStep2();
      if (Object.keys(e).length) { setErrors(e); return; }
      setSubmitting(true);
      try {
        const numerology = fullNumerology(form.fullName, form.dob);
        numerology.zodiac = form.zodiac || numerology.zodiac;
        const { dominant } = determineDominantElement(numerology.lifePath, numerology.nameNumber, numerology.zodiac);
        const panchaBeads = selectPanchaBeads(dominant);
        const prescription = buildPrescription({
          lifePath: numerology.lifePath,
          nameNumber: numerology.nameNumber,
          zodiac: numerology.zodiac,
          intentions: form.intentions.map((label) => INTENTIONS.find((i) => i.label === label)?.key).filter(Boolean),
          panchaBeads,
          wristSize: form.wristSize,
        });
        if (orderId) {
          await quizApi.step3(orderId, {
            zodiac: form.zodiac,
            intentions: form.intentions,
            wristSize: form.wristSize,
            numerology,
            panchaBeads,
            prescriptionCrystals: prescription,
            dominantElement: dominant,
          });
        }
        toast.success("Your prescription is ready");
        navigate(`/reveal/${orderId}`);
      } catch {
        toast.error("Could not create prescription. Please try again.");
        setSubmitting(false);
      }
    }
  };

  const goBack = () => {
    if (step > 1) {
      setSlideDir("back");
      setStep(step - 1);
    }
  };

  const slideClass = slideDir === "next" ? "pf-slide-in-right" : "pf-slide-in-left";

  return (
    <div className="pf-wizard-root min-h-screen" data-testid="prescription-form-page">
      <Particles variant="violet" />
      {submitting && <LotusLoader context="quiz" />}
      <BrandHeader />

      <div className="relative max-w-2xl mx-auto px-5 md:px-8 py-10 md:py-14">
        {showResumeBanner && resumeData?.fullName && (
          <ResumeBanner name={resumeData.fullName} onClose={() => setShowResumeBanner(false)} />
        )}
        <ProgressIndicator step={step} isResume={isResume} />

        <div key={step} className={slideClass}>
          {step === 1 && (
            <Step1
              form={form}
              errors={errors}
              update={update}
              onContinue={goNext}
              valid={step1Valid}
              prefilled={prefilled}
              phoneState={phoneState}
              setPhoneState={setPhoneState}
            />
          )}
          {step === 2 && (
            <Step2
              form={form}
              errors={errors}
              update={update}
              toggleIntention={toggleIntention}
              onSubmit={goNext}
              onBack={goBack}
              valid={step2Valid}
              prefilled={prefilled}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Subcomponents ============

function ResumeBanner({ name, onClose }) {
  const first = (name || "").trim().split(/\s+/)[0] || "back";
  return (
    <div className="pf-resume-banner" data-testid="resume-banner">
      <button type="button" onClick={onClose} className="pf-resume-close" aria-label="Dismiss" data-testid="resume-banner-close">&times;</button>
      <div className="font-display text-white text-lg md:text-xl">Welcome back, {first}</div>
      <div className="font-serif italic text-sm mt-1" style={{ color: "#D4A855" }}>
        Your prescription is exactly where you left it
      </div>
    </div>
  );
}

function ProgressIndicator({ step, isResume }) {
  return (
    <div className="text-center mb-10" data-testid="pf-progress">
      <div className="flex items-center justify-center gap-3">
        {[1, 2].map((n) => (
          <span
            key={n}
            className="pf-dot"
            style={{ background: n <= step ? "#E8B547" : "rgba(212,168,85,0.3)" }}
            data-filled={n <= step}
          />
        ))}
      </div>
      <div className="mt-3 text-xs tracking-[0.28em] uppercase" style={{ color: "#E8B547" }}>
        Step {step} of 2{isResume && step === 2 ? " (almost there!)" : ""}
      </div>
    </div>
  );
}

function StepHeader({ heading, sub }) {
  return (
    <div className="text-center">
      <h1 className="font-display text-3xl md:text-4xl text-white leading-tight">{heading}</h1>
      <p className="font-serif italic mt-3" style={{ color: "#FAF7F2", fontSize: "16px" }}>{sub}</p>
    </div>
  );
}

function Step1({ form, errors, update, onContinue, valid, prefilled, phoneState, setPhoneState }) {
  return (
    <div className="pf-step" data-testid="pf-step-1">
      <StepHeader heading="Let's begin with the basics" sub="So we know how to send your prescription" />
      <form
        onSubmit={(e) => { e.preventDefault(); if (valid) onContinue(); }}
        className="pf-card mt-8 p-6 md:p-8 space-y-5"
      >
        <Field label="Full Name" required error={errors.fullName} name="fullName" preFilled={prefilled?.has("fullName")}>
          <input
            type="text"
            className={`pf-input ${errors.fullName ? "pf-input-err" : ""}`}
            value={form.fullName}
            onChange={(e) => update("fullName", e.target.value)}
            placeholder="As on your passport"
            data-testid="input-fullName"
            autoFocus
          />
        </Field>
        <Field label="Email" required error={errors.email} name="email" preFilled={prefilled?.has("email")}>
          <input
            type="email"
            className={`pf-input ${errors.email ? "pf-input-err" : ""}`}
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            placeholder="you@example.com"
            data-testid="input-email"
          />
        </Field>
        <Field label="Phone (WhatsApp)" required error={errors.whatsapp} name="whatsapp" preFilled={prefilled?.has("whatsapp")}>
          <CountryPhoneInput
            value={form.whatsapp}
            hasError={!!errors.whatsapp}
            onChange={(full, info) => {
              setPhoneState({ full, country: info.country, digits: info.digits, isValid: info.isValid });
              update("whatsapp", full);
            }}
            testid="input-whatsapp"
          />
          {phoneState.country && phoneState.country.code !== "IN" && (
            <p className="font-serif italic mt-2" style={{ color: "#D4A855", fontSize: "13px" }} data-testid="intl-shipping-note">
              We ship internationally — additional shipping fees apply.
            </p>
          )}
        </Field>
        <Field label="Date of Birth" required error={errors.dob} name="dob" preFilled={prefilled?.has("dob")}>
          <div className="pf-dob-wrap">
            <input
              type="date"
              className={`pf-input ${errors.dob ? "pf-input-err" : ""}${!form.dob ? " pf-input--dob-empty" : ""}`}
              value={form.dob || "1995-01-01"}
              min="1930-01-01"
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => {
                const v = e.target.value;
                // iOS may return empty on picker cancel — only update when a real date is chosen
                if (v) update("dob", v);
              }}
              data-testid="input-dob"
              autoComplete="bday"
              aria-label="Date of Birth"
            />
            {!form.dob && (
              <span className="pf-dob-placeholder" aria-hidden data-testid="input-dob-placeholder">
                DD / MM / YYYY
              </span>
            )}
          </div>
        </Field>
        <button
          type="submit"
          disabled={!valid}
          className="pf-cta w-full mt-3"
          data-testid="pf-continue-1"
        >
          Continue <span aria-hidden>&rarr;</span>
        </button>
        <p className="text-center text-xs" style={{ color: "rgba(250,247,242,0.7)" }}>
          Step 2: Your zodiac &amp; intentions
        </p>
      </form>
    </div>
  );
}

function Step2({ form, errors, update, toggleIntention, onSubmit, onBack, valid, prefilled }) {
  return (
    <div className="pf-step" data-testid="pf-step-2">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="pf-back-btn" data-testid="pf-back-2">&larr; Back</button>
        <span />
      </div>
      <StepHeader heading="One last layer for your reading" sub="This adds your personality overlay" />
      <form
        onSubmit={(e) => { e.preventDefault(); if (valid) onSubmit(); }}
        className="pf-card mt-8 p-6 md:p-8 space-y-6"
      >
        <Field label="Zodiac Sign" required error={errors.zodiac} name="zodiac" hint="Auto-detected from your birth date" preFilled={prefilled?.has("zodiac")}>
          <select
            className={`pf-input ${errors.zodiac ? "pf-input-err" : ""}`}
            value={form.zodiac}
            onChange={(e) => update("zodiac", e.target.value)}
            data-testid="input-zodiac"
          >
            <option value="">Select...</option>
            {ZODIAC_LIST.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </Field>

        <Field label="Healing Intentions" required error={errors.intentions} name="intentions">
          <p className="font-serif italic" style={{ color: "#D4A855", fontSize: "13px", marginTop: "6px", marginBottom: "16px" }}>
            Choose up to {MAX_INTENTIONS} &mdash; we&apos;ll prescribe the crystals that matter most.
            {form.intentions.length > 0 && (
              <span className="ml-2" data-testid="intentions-counter" style={{ color: "#6A0DAD", fontStyle: "normal" }}>
                ({form.intentions.length}/{MAX_INTENTIONS})
              </span>
            )}
          </p>
          <div className="pf-chip-grid" data-testid="intentions-list">
            {INTENTIONS.map((i) => {
              const active = form.intentions.includes(i.label);
              const atCap = !active && form.intentions.length >= MAX_INTENTIONS;
              return (
                <button
                  type="button"
                  key={i.key}
                  onClick={() => toggleIntention(i.label)}
                  disabled={atCap}
                  aria-disabled={atCap}
                  className={`pf-chip ${active ? "pf-chip-active" : ""} ${atCap ? "pf-chip-disabled" : ""}`}
                  data-testid={`chip-${i.key}`}
                  aria-pressed={active}
                >
                  <span className="pf-chip-icon" aria-hidden>
                    {active ? "\u2713" : i.icon}
                  </span>
                  <span className="pf-chip-label">{i.label}</span>
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Wrist Size" required error={errors.wristSize} name="wristSize" preFilled={prefilled?.has("wristSize")}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-1">
            {WRIST_OPTIONS.map((w) => {
              const active = form.wristSize === w.key;
              return (
                <button
                  type="button"
                  key={w.key}
                  onClick={() => update("wristSize", w.key)}
                  className={`pf-pill ${active ? "pf-pill-active" : ""}`}
                  data-testid={`wrist-${w.key}`}
                  aria-pressed={active}
                >
                  <div className="pf-pill-h">{w.label}</div>
                  <div className="pf-pill-s">{w.sub}</div>
                </button>
              );
            })}
          </div>
          <p className="text-center font-serif italic mt-3" style={{ color: "#D4A855", fontSize: "13px" }}>
            Not sure? We&rsquo;ll confirm sizing on WhatsApp before crafting.
          </p>
        </Field>

        <button type="submit" disabled={!valid} className="pf-cta w-full mt-2" data-testid="pf-submit-3">
          Reveal My Prescription <span aria-hidden>&rarr;</span>
        </button>
      </form>
    </div>
  );
}

function Field({ label, required, error, children, name, hint, preFilled }) {
  return (
    <div data-field={name}>
      <label className="block text-sm font-medium mb-2" style={{ color: "#FAF7F2" }}>
        {label}
        {required && <span className="ml-1" style={{ color: "#CC4455" }} aria-hidden>*</span>}
        {hint && <span className="ml-2 text-xs" style={{ color: "rgba(250,247,242,0.6)" }}>({hint})</span>}
        {preFilled && (
          <span className="pf-prefilled-marker ml-2" data-testid={`prefilled-${name}`}>
            <span aria-hidden>&#10003;</span> Already known from your previous visit
          </span>
        )}
      </label>
      {children}
      {error && <p className="mt-1.5 text-sm" style={{ color: "#E88" }} data-testid={`error-${name}`}>{error}</p>}
    </div>
  );
}
